import pick from "lodash/pick";
import {
  ConversationFilterConfig,
  ConversationInboxState,
  normalizeInboxConversationTypes,
} from "reducers/conversationInboxReducer";
import { AccountType } from "shared/types/accounts";
import {
  Conversation,
  Message,
  ConversationsFilters,
  ConversationType,
  TypingCollaborator,
  MessageType,
  Status,
  Tag,
} from "types/conversationInbox";
import { ActivitiesType } from "../@inbox/pages/home/model";

type MainState = {
  conversationInbox: ConversationInboxState;
  entities: {
    accounts: {
      byId: Record<string, AccountType>;
      allIds?: string[];
      selectedIds?: string[];
      withToken?: string[];
      counts?: unknown;
    };
    users?: unknown;
    accountGroups?: unknown;
    collections?: unknown;
    posts?: unknown;
  };
};

export type WrappedInboxState = MainState;

export const getConversations = (state: MainState): Conversation[] => {
  return state.conversationInbox.conversations ?? [];
};
export const getConversationsInBucket = (
  state: MainState,
  bucket: ActivitiesType
): Conversation[] => {
  return (state.conversationInbox.conversations ?? []).filter((c) => {
    if (bucket === ActivitiesType.closed) {
      return c.status === Status.closed;
    }
    return c.status !== Status.closed;
  });
};

export const getConversation = (
  state: MainState,
  conversationId: string
): Conversation | null => {
  const conversations = state.conversationInbox?.conversations ?? [];
  if (conversationId === "first") {
    return conversations[0];
  }
  return conversations.find((c) => c.id === conversationId) || null;
};

export const getConversationReplyMessageType = (
  state: MainState,
  conversationId: string
): MessageType | null => {
  const conversation = getConversation(state, conversationId);
  const account = conversation?.account
    ? state.entities.accounts.byId[conversation.account]
    : undefined;

  if (conversation?.type && account) {
    const accountPlatform = account.platformType || "IG";
    switch (conversation.type) {
      case "MESSAGE": {
        switch (accountPlatform) {
          case "IG": {
            return MessageType.igMessage;
          }
          case "FB": {
            return MessageType.fbMessage;
          }
        }
        break;
      }
      case "QA": {
        switch (accountPlatform) {
          case "GMB": {
            return MessageType.gmbAnwser;
          }
        }
        break;
      }
      case "REVIEW": {
        switch (accountPlatform) {
          case "GMB": {
            return MessageType.gmbReviewReply;
          }
        }
        break;
      }
      case "POST":
      case "COMMENT":
      case "MENTION":
      case "MENTION_V2":
      case "TAGGED_POST": {
        switch (accountPlatform) {
          case "IG": {
            return MessageType.igComment;
          }
          case "FB": {
            return MessageType.fbComment;
          }
          case "LI": {
            return MessageType.liComment;
          }
        }
        break;
      }
      case "THREADED_COMMENT": {
        switch (accountPlatform) {
          case "IG": {
            return MessageType.igThreadedComment;
          }
          case "FB": {
            return MessageType.fbThreadedComment;
          }
          case "LI": {
            return MessageType.liThreadedComment;
          }
          case "TT": {
            return MessageType.ttThreadedComment;
          }
        }
        break;
      }
    }
  }

  const firstNonSysmtemMessage = (conversation?.messages || []).find(
    (message) =>
      ![
        MessageType.system,
        MessageType.typing,
        MessageType.internal,
        MessageType.messageCreating,
      ].includes(message.type) && !message.isInternal
  );

  const result = firstNonSysmtemMessage ? firstNonSysmtemMessage.type : null;

  return result;
};

export const getMessages = (
  state: MainState,
  conversationId: string
): Message[] => {
  const conversation = getConversation(state, conversationId);
  return conversation?.messages || [];
};

export const isMessagesFetched = (
  state: MainState,
  conversationId: string
): boolean => {
  const conversation = getConversation(state, conversationId);

  return !conversation || !!conversation.isMessagesFetched;
};
export const isMessageReactionProcessing = (
  state: MainState,
  conversationId: string,
  messageId: string
): boolean => {
  const messages = getMessages(state, conversationId);
  return !!messages.find((c) => c.id === messageId)?.isReationProcessing;
};

export const getIsMessageLoading = (
  state: MainState,
  conversationId: string
): boolean =>
  !!state.conversationInbox.conversations?.find((c) => c.id === conversationId)
    ?.isMessagesLoading;
export const getIsMessageFetchedError = (
  state: MainState,
  conversationId: string
): boolean =>
  !!state.conversationInbox.conversations?.find((c) => c.id === conversationId)
    ?.isLastMessagesFetchedError;

export const getMessagesHasNext = (
  state: MainState,
  conversationId: string
): boolean => {
  const conversation: Conversation | null = getConversation(
    state,
    conversationId
  );
  return conversation?.hasNext ?? false;
};

export const getMessagesNext = (
  state: MainState,
  conversationId: string
): string | null => {
  const conversation: Conversation | null = getConversation(
    state,
    conversationId
  );
  return conversation?.next ?? null;
};
export const getIsConversationCountersLoading = (state: MainState): boolean =>
  state.conversationInbox.isConversationCountersLoading;
export const getIsConversationLoading = (state: MainState): boolean =>
  !!(
    state.conversationInbox.isConversationLoading ||
    state.conversationInbox.isClosingAllConversationRequest
  );
export const getIsConversationClosing = (state: MainState): boolean =>
  !!state.conversationInbox.isClosingAllConversationRequest;

export const getIsConversationJustCleared = (state: MainState): boolean =>
  state.conversationInbox.isConversationJustCleared;

export const getConversationFilterConfig = (
  state: MainState
): ConversationFilterConfig => {
  return pick(state.conversationInbox, [
    "isStarred",
    "isMentioned",
    "assignedTo",
    "type",
    "status",
    "unassigned",
  ]) as ConversationFilterConfig;
};

export const getConversationNext = (state: MainState): string | null =>
  state.conversationInbox.next;
export const getConversationHasNext = (state: MainState): boolean =>
  state.conversationInbox.hasNext;
export const getConversationStarred = (state: MainState): boolean =>
  state.conversationInbox.isStarred;
export const getConversationMentioned = (state: MainState): boolean =>
  state.conversationInbox.isMentioned ?? false;
export const getConversationSort = (state: MainState): string =>
  state.conversationInbox.sort;

export const getSelectedConversationAccounts = (state: MainState): string[] =>
  state.conversationInbox.selectedAccounts ?? [];
export const getConversationAssignedTo = (state: MainState): string | null =>
  state.conversationInbox.assignedTo;
export const getConversationTypes = (state: MainState): ConversationType[] =>
  normalizeInboxConversationTypes(state.conversationInbox.types);

export const getConversationStatus = (state: MainState) =>
  state.conversationInbox.status;
export const getConversationUnassigned = (state: MainState): boolean | null =>
  state.conversationInbox.unassigned;
export const getConversationAwaitingReply = (state: MainState): boolean =>
  state.conversationInbox.awaitingReply ?? false;
export const getConversationsCounters = (
  state: MainState
): ConversationsFilters => ({
  all: state.conversationInbox.counter.all,
  allCapped: state.conversationInbox.counter.allCapped,
  unassigned: state.conversationInbox.counter.unassigned,
  unassignedCapped: state.conversationInbox.counter.unassignedCapped,
  assignedToMe: state.conversationInbox.counter.assignedToMe,
  assignedToMeCapped: state.conversationInbox.counter.assignedToMeCapped,
  mentioned: state.conversationInbox.counter.mentioned,
  mentionedCapped: state.conversationInbox.counter.mentionedCapped,
  starred: state.conversationInbox.counter.starred,
  starredCapped: state.conversationInbox.counter.starredCapped,
  closed: state.conversationInbox.counter.closed,
  closedCapped: state.conversationInbox.counter.closedCapped,
  unreadConversations: state.conversationInbox.counter.unreadConversations,
  unreadConversationsCapped:
    state.conversationInbox.counter.unreadConversationsCapped,
  taggedInPost: state.conversationInbox.counter.taggedInPost,
  taggedInPostCapped: state.conversationInbox.counter.taggedInPostCapped,
  awaitingReply: state.conversationInbox.counter.awaitingReply,
  awaitingReplyCapped: state.conversationInbox.counter.awaitingReplyCapped,
});

export const getTotalUnreadCount = (state: MainState) =>
  (state.conversationInbox.conversations ?? []).reduce(
    (totalCount: number, conversation: Conversation) => {
      return totalCount + conversation.unreadCount;
    },
    0
  );

export const getInboxTypingCollaborators = (state: {
  conversationInbox: ConversationInboxState;
}): TypingCollaborator[] => state.conversationInbox.typingCollaborators || [];

export const getSelectedConversations = (state: MainState): string[] =>
  state.conversationInbox?.selectedConversations || [];

export const getAccountsByIds = (
  state: MainState,
  ids: string[]
): AccountType[] => {
  return ids.map((id) => state.entities.accounts.byId[id]).filter(Boolean);
};

export const getEditingMessage = (state: MainState): Message | null => {
  return state.conversationInbox.editingMessage;
};

export const getConversationAccountContext = (
  state: MainState,
  conversationId: string
) => {
  const conversation = getConversation(state, conversationId);
  return conversation?.account
    ? state.entities.accounts.byId[conversation.account]
    : undefined;
};

export const getSearchTerm = (state: MainState): string =>
  state.conversationInbox.searchTerm ?? "";
export const getSentimentFilter = (state: MainState): string[] =>
  state.conversationInbox.sentiment ?? [];
export const getVisibleFacets = (state: MainState): string[] =>
  state.conversationInbox.visibleFacets ?? ["status", "type", "assigned"];
export const getTagFilter = (state: MainState): string[] =>
  state.conversationInbox.tagFilter ?? [];
export const getDateFrom = (state: MainState): string | null =>
  state.conversationInbox.dateFrom ?? null;
export const getDateTo = (state: MainState): string | null =>
  state.conversationInbox.dateTo ?? null;
export const getWorkspaceTags = (state: MainState): Tag[] =>
  state.conversationInbox.workspaceTags ?? [];
export const getIsWorkspaceTagsLoading = (state: MainState): boolean =>
  state.conversationInbox.isWorkspaceTagsLoading ?? false;
export const getWorkspaceTagsError = (state: MainState): string | null =>
  state.conversationInbox.workspaceTagsError ?? null;
export const getArchivedTags = (state: MainState): Tag[] =>
  state.conversationInbox.archivedTags ?? [];
export const getIsArchivedTagsLoading = (state: MainState): boolean =>
  state.conversationInbox.isArchivedTagsLoading ?? false;
export const getArchivedTagsError = (state: MainState): string | null =>
  state.conversationInbox.archivedTagsError ?? null;
export const getRestoringTagIds = (state: MainState): string[] =>
  state.conversationInbox.restoringTagIds ?? [];
